import { useState } from 'react';

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: 'Is Garden State Junk Removal licensed and insured?',
      answer: 'Yes, we are fully licensed and insured for your protection and peace of mind. We carry comprehensive liability insurance and workers compensation coverage. Our team follows all safety protocols and regulations to ensure safe removal of your items without damage to your property.'
    },
    {
      question: 'What items do you accept for removal?',
      answer: 'We accept most household and commercial items including furniture, appliances, electronics, mattresses, construction debris, yard waste, and general junk. However, we cannot accept hazardous materials such as chemicals, paint, asbestos, or medical waste. If you are unsure about a specific item, please contact us and we will be happy to advise you.'
    },
    {
      question: 'How does your pricing work?',
      answer: 'Our pricing is based on the volume of items you need removed, measured in truck space. We offer transparent, upfront pricing with no hidden fees. The price includes all labor, loading, hauling, disposal fees, and recycling costs. We provide free estimates and the final price is confirmed before we begin any work.'
    },
    {
      question: 'Do you recycle or donate items?',
      answer: 'Absolutely! Environmental responsibility is a core value of our business. We recycle or donate approximately 95% of the items we collect. Usable furniture and appliances are donated to local charities, metals are sent to recycling facilities, and we ensure proper disposal of electronics and other materials according to environmental regulations.'
    },
    {
      question: 'How quickly can you schedule a pickup?',
      answer: 'We offer flexible scheduling including same-day and next-day service in most cases. When you contact us for an estimate, we will work with you to find a convenient time that fits your schedule. For urgent removals, we do our best to accommodate your needs as quickly as possible.'
    },
    {
      question: 'Do I need to be present during the removal?',
      answer: 'While it is helpful for you to be present to point out items and answer any questions, it is not always necessary. If you cannot be there, you can provide clear instructions and photos of the items to be removed. We will ensure everything is handled professionally and securely whether you are present or not.'
    },
    {
      question: 'What safety measures do you take?',
      answer: 'Safety is our top priority. Our trained crew uses proper lifting techniques and equipment to prevent injuries. We take precautions to protect your property including using floor coverings, padding doorways, and carefully navigating tight spaces. All team members are background-checked and trained in safe junk removal practices.'
    },
    {
      question: 'What areas do you serve?',
      answer: 'We proudly serve the entire state of New Jersey — the Garden State! From North Jersey to the Shore and everywhere in between, our team is ready to help with junk removal, moving, and packing. If you are anywhere in New Jersey, we have got you covered. Contact us to schedule your appointment today.'
    },
    {
      question: 'How do I prepare for junk removal service?',
      answer: 'Preparation is minimal! Simply identify the items you want removed and ensure our team has clear access to them. If items are in a basement or upstairs, that is no problem - we handle all the heavy lifting. You do not need to move anything or prepare items in any special way. Just point and we will take care of the rest.'
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards, debit cards, cash, and checks for your convenience. Payment is due upon completion of the service. We will provide you with a detailed receipt showing all items removed and the final cost. No payment is required when you request your free estimate.'
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-teal-100 px-4 py-2 rounded-full mb-4">
            <i className="ri-question-line text-teal-600 text-lg"></i>
            <span className="text-teal-700 text-sm font-semibold">FAQ</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Find answers to common questions about our junk removal services, safety protocols, recycling policies, and more.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-gray-50 rounded-xl overflow-hidden border border-gray-200 hover:border-teal-600 transition-colors"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
              >
                <h4 className="text-lg font-bold text-gray-900 pr-4">
                  {faq.question}
                </h4>
                <div className={`w-8 h-8 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform ${openIndex === index ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-white text-xl"></i>
                </div>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="px-6 pb-5">
                  <p className="text-gray-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-8 md:p-12 text-center">
          <h3 className="text-3xl font-bold text-white mb-4">
            Still Have Questions?
          </h3>
          <p className="text-teal-100 text-lg mb-6 max-w-2xl mx-auto">
            Our friendly team is here to help! Contact us anytime and we will be happy to answer any questions you have about our junk removal services.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:+19089021076"
              className="bg-white text-teal-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center space-x-2 whitespace-nowrap"
            >
              <i className="ri-phone-line text-xl"></i>
              <span>Call (908) 902-1076</span>
            </a>
            <a
              href="#booking"
              className="bg-teal-800 text-white px-8 py-3 rounded-lg font-semibold hover:bg-teal-900 transition-colors whitespace-nowrap"
            >
              Request Free Estimate
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
